import React, {
	useMemo,
	useState,
	useEffect,
	useCallback,
	useRef
} from 'react';
import allCountriesList from "../../../utils/constants/allCountriesList";
import { useTranslation } from "react-i18next";
import CompaniesService from "../../../services/Companies/CompaniesService";
import toTitleCase from "../../../utils/functions/toTitleCase";
import formatSireXNumber from "../../../utils/functions/formatSireXNumber";
import removeSpaces from "../../../utils/functions/removeSpaces";
import formatNBTVA from "../../../utils/functions/formatNBTVA";
import axios from 'axios';
import AzureMapsService from "../../../services/AzureMaps/AzureMapsService";
import TextInputMobile from "../atomicDesignMobile/atoms/TextInputMobile";
import { useAuthContext } from "../../../contexts/AuthContext";
import CountrySelectInputMobile from "../atomicDesignMobile/CountrySelectInputMobile";
import { CompanyLookupTelemetry } from '../../../types';


const SignUpPageMobileProPanel2 = () => {
	const {
		userInfo,
		setUserInfo,
		isSecondPanelValidated,
		validateSecondPanel,
		syncExistingCompanyMatchByVAT,
		activateExistingCompanyAttachment,
		openedPanel,
		setOpenedPanel
	} = useAuthContext();
	const { t, i18n } = useTranslation();
	const [fetchingCompanyData, setFetchingCompanyData] = useState(false);
	const [showClassicCompanyForm, setShowClassicCompanyForm] = useState(false);
	const [disableFields, setDisableFields] = useState({
		nbtva: false,
		companyName: false,
	});
	const [wrongFields, setWrongFields] = useState({
		siret: false,
		siren: false,
		postalCode: false,
		nbtva: false,
		city: false,
		address: false,
		companyName: false,
	});
	const [addressSuggestions, setAddressSuggestions] = useState([]);
	const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
	const autocompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lookupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const isFR = userInfo.company.countryCode === 'FR';
	const identifierValue = isFR ? userInfo.company.siret : userInfo.company.nbtva;
	const isReducedLookupMode = !showClassicCompanyForm;
	const lookupTelemetry = useMemo<CompanyLookupTelemetry>(
		() => ({
			lookupMode: isReducedLookupMode ? 'reduced' : 'classic',
			countryCode: userInfo.company.countryCode,
			identifierType: 'vat',
		}),
		[isReducedLookupMode, userInfo.company.countryCode]
	);
	
	useEffect(() => {
		const cleanIdentifier = removeSpaces(identifierValue);

		if (lookupTimeoutRef.current) {
			clearTimeout(lookupTimeoutRef.current);
		}

		if (!cleanIdentifier) {
			setShowClassicCompanyForm(false);
			setDisableFields({
				nbtva: false,
				companyName: false,
			});
				void syncExistingCompanyMatchByVAT(undefined, lookupTelemetry);
			return;
		}

		const isLookupReady = isFR ? cleanIdentifier.length === 14 : cleanIdentifier.length >= 8;
		if (!isLookupReady) {
			setShowClassicCompanyForm(false);
			setDisableFields({
				nbtva: false,
				companyName: false,
			});
				void syncExistingCompanyMatchByVAT(undefined, lookupTelemetry);
			return;
		}

		lookupTimeoutRef.current = setTimeout(() => {
			const fetchCompanyData = async () => {
				setFetchingCompanyData(true);
				try {
					if (isFR) {
						const { data } = await CompaniesService.sirenInformationRead(cleanIdentifier.substring(0, 9));
						if (data.denominationUniteLegale === null || data.denominationUniteLegale.includes("[ND] [ND]")) {
							setDisableFields({
								nbtva: false,
								companyName: false,
							});
							await syncExistingCompanyMatchByVAT(undefined, lookupTelemetry);
							setShowClassicCompanyForm(true);
							return;
						}

						setDisableFields({
							nbtva: true,
							companyName: true,
						});
						setUserInfo((prevState: any) => ({
							...prevState,
							company: {
								...prevState.company,
								siret: data.siret || prevState.company.siret,
								siren: data.siren || prevState.company.siren,
								companyName: toTitleCase(data.denominationUniteLegale) || prevState.company.companyName,
								nbtva: data.tva || prevState.company.nbtva,
							},
						}));
						const matchedCompany = await syncExistingCompanyMatchByVAT(data.tva, lookupTelemetry);
						if (matchedCompany) {
							activateExistingCompanyAttachment(matchedCompany);
							return;
						}
						setShowClassicCompanyForm(true);
						return;
					}

					setDisableFields({
						nbtva: false,
						companyName: false,
					});
					const matchedCompany = await syncExistingCompanyMatchByVAT(cleanIdentifier, lookupTelemetry);
					if (matchedCompany) {
						activateExistingCompanyAttachment(matchedCompany);
						return;
					}
					setShowClassicCompanyForm(true);
				} catch (error) {
					console.error('Erreur lors de la récupération des informations de l\'entreprise', error);
					setDisableFields({
						nbtva: false,
						companyName: false,
					});
					setShowClassicCompanyForm(true);
				} finally {
					setFetchingCompanyData(false);
				}
			};

			void fetchCompanyData();
		}, 350);

		return () => {
			if (lookupTimeoutRef.current) {
				clearTimeout(lookupTimeoutRef.current);
			}
		};
	}, [activateExistingCompanyAttachment, identifierValue, isFR, lookupTelemetry, setUserInfo, syncExistingCompanyMatchByVAT]);

	useEffect(() => {
		if (!userInfo.company.countryCode) {
			setUserInfo((prevState: any) => ({
				...prevState,
				company: {
					...prevState.company,
					countryCode: 'FR',
				},
			}));
		}
	}, [setUserInfo, userInfo.company.countryCode]);
	
	const validateFields = () => {
		const newWrongFields = {
			siret: (isFR && userInfo.company.siret.length !== 14) || false,
			siren: (isFR && userInfo.company.siren.length !== 9) || false,
			postalCode: (isFR && userInfo.company.postalCode.length !== 5) || userInfo.company.postalCode === '',
			nbtva: userInfo.company.nbtva === '',
			city: userInfo.company.city === '',
			address: userInfo.company.address === '',
			companyName: userInfo.company.companyName === '',
		};
		
		setWrongFields(newWrongFields);
		
		return Object.values(newWrongFields).every(value => !value);
	};
	
	const handleAddressAutocomplete = useCallback(async (query: string) => {
			const source = axios.CancelToken.source();
			
			try {
				const response = await AzureMapsService.getAddressSuggestions(query, { cancelToken: source.token });
				const suggestions = response.data.results.map((result: any) => ({
					label: result.address.freeformAddress,
					city: result.address.municipality,
					postalCode: result.address.postalCode,
				}));
				setAddressSuggestions(suggestions);
				setShowAddressSuggestions(true);
			} catch (error) {
				if (axios.isCancel(error)) {
					console.log("Request canceled", error.message);
				} else {
					console.error("Erreur lors de la récupération des suggestions d'adresse", error);
				}
			}
		}
		, []);
	
	const handleAddressChange = (e: any) => {
		const newAddress = e.target.value;
		
		setUserInfo((prevState: any) => ({
			...prevState,
			company: {
				...prevState.company,
				address: newAddress,
			},
		}));
		
		if (autocompleteTimeoutRef.current) {
			clearTimeout(autocompleteTimeoutRef.current);
		}
		
		autocompleteTimeoutRef.current = setTimeout(() => {
			handleAddressAutocomplete(newAddress);
		}, 400);
	};
	
	const handleSelectSuggestion = (suggestion: any) => {
		setShowAddressSuggestions(false);
		const [street] = suggestion.label.split(',');
		setUserInfo((prevState: any) => ({
			...prevState,
			company: {
				...prevState.company,
				address: street.trim(),
				city: suggestion.city,
				postalCode: suggestion.postalCode,
			},
		}));
	};
	
	const blockAddress = useMemo(() => {
		return (
			<div style={{ position: 'relative' }}>
				<TextInputMobile
					width={260}
					type="text"
					value={userInfo.company.address}
					placeholder="6 rue des Lys"
					title={t('secondPanel:address')}
					mandatory
					onChange={handleAddressChange}
					wrong={wrongFields.address}
					wrongMessage={t('secondPanel:fill_this_field')}
				/>
				{showAddressSuggestions && (
					<div style={{
						position: 'absolute',
						width: 426,
						backgroundColor: '#fff',
						border: '1px solid #ccc',
						maxHeight: 160,
						overflowY: 'auto',
						zIndex: 10
					}}>
						{addressSuggestions.map((suggestion: any, index) => (
							<div
								key={index}
								onClick={() => handleSelectSuggestion(suggestion)}
								style={{
									padding: 10,
									cursor: 'pointer',
									borderBottom: '1px solid #eee'
								}}>
								{suggestion.label}
							</div>
						))}
					</div>
				)}
			</div>
		);
	}, [userInfo.company.address, wrongFields.address, addressSuggestions, t]);
	
	const blockCity = useMemo(() => {
		return (
			<TextInputMobile
				width={124}
				type="text"
				value={userInfo.company.city}
				placeholder="Grenoble"
				title={t('secondPanel:city')}
				mandatory
				onChange={(e) =>
					setUserInfo((prevState: any) => ({
						...prevState,
						company: {
							...prevState.company,
							city: toTitleCase(e.target.value),
						},
					}))
				}
				wrong={wrongFields.city}
				wrongMessage={t('secondPanel:fill_this_field')}
			/>
		);
	}, [userInfo.company.city, wrongFields.city, t]);
	
	const blockCompanyName = useMemo(() => {
		return (
			<TextInputMobile
				width={260}
				type="text"
				value={userInfo.company.companyName}
				placeholder="Garage Dupont"
				title={t('secondPanel:company_name')}
				mandatory
				disabled={disableFields.companyName}
				loading={fetchingCompanyData}
				onChange={(e) =>
					setUserInfo((prevState: any) => ({
						...prevState,
						company: {
							...prevState.company,
							companyName: toTitleCase(e.target.value),
						},
					}))
				}
				wrong={wrongFields.companyName}
				wrongMessage={t('secondPanel:fill_this_field')}
			/>
		);
	}, [userInfo.company.companyName, fetchingCompanyData, wrongFields.companyName, disableFields.companyName, t]);
	
	const blockNbtva = useMemo(() => {
		return (
			<TextInputMobile
				width={userInfo.company.countryCode === 'FR' ? 124 : 260}
				type="text"
				value={formatNBTVA(userInfo.company.nbtva)}
				placeholder="FR 12 123456789"
				title={t('secondPanel:vat_number_mobile')}
				mandatory
				disabled={disableFields.nbtva}
				loading={fetchingCompanyData}
				onChange={(e) =>
					setUserInfo((prevState: any) => ({
						...prevState,
						company: {
							...prevState.company,
							nbtva: removeSpaces(e.target.value).toUpperCase(),
						},
					}))
				}
				wrong={wrongFields.nbtva}
				wrongMessage={t('secondPanel:fill_this_field')}
			/>
		);
	}, [userInfo.company.nbtva, userInfo.company.countryCode, fetchingCompanyData, wrongFields.nbtva, disableFields.nbtva, t]);
	
	const blockPostalCode = useMemo(() => {
		return (
			<TextInputMobile
				width={124}
				type="text"
				value={userInfo.company.postalCode}
				placeholder="38000"
				title={t('secondPanel:postal_code')}
				mandatory
				onChange={(e) => {
					const newValue = e.target.value;
					if ((userInfo.company.countryCode === 'FR' && /^\d*$/.test(newValue)) || userInfo.company.countryCode !== 'FR') {
						setUserInfo((prevState: any) => ({
							...prevState,
							company: {
								...prevState.company,
								postalCode: newValue,
							},
						}));
					}
				}}
				wrong={wrongFields.postalCode}
				wrongMessage={t('secondPanel:wrong_postal_code')}
			/>
		);
	}, [userInfo.company.postalCode, wrongFields.postalCode, t]);
	
	const blockSiren = useMemo(() => {
		return (
			<TextInputMobile
				width={124}
				type="text"
				value={formatSireXNumber(userInfo.company.siren)}
				placeholder="123 456 789"
				title={t('secondPanel:siren')}
				mandatory
				disabled
				wrong={wrongFields.siren}
				wrongMessage={t('secondPanel:wrong_siren')}
			/>
		);
	}, [userInfo.company.siren, wrongFields.siren,t]);
	
	const blockIdentifier = useMemo(() => {
		if (isFR) {
			return (
				<TextInputMobile
					width={260}
					type="text"
					value={formatSireXNumber(userInfo.company.siret)}
					placeholder="123 456 789 00001"
					title={t('secondPanel:siret')}
					mandatory
					loading={fetchingCompanyData}
					onChange={(e) => {
						const newValue = removeSpaces(e.target.value);
						if (/^\d*$/.test(newValue)) {
							if (newValue.length !== 14) {
								void syncExistingCompanyMatchByVAT(undefined, lookupTelemetry);
							}
							setUserInfo((prevState: any) => ({
								...prevState,
								company: {
									...prevState.company,
									siret: newValue,
									siren: newValue.length === 14 ? newValue.substring(0, 9) : '',
									nbtva: '',
								},
							}));
						}
					}}
					wrong={wrongFields.siret}
					wrongMessage={t('secondPanel:wrong_siret')}
				/>
			);
		}

		return (
			<TextInputMobile
				width={260}
				type="text"
				value={formatNBTVA(userInfo.company.nbtva)}
				placeholder="FR 12 123456789"
				title={t('secondPanel:vat_number_mobile')}
				mandatory
				loading={fetchingCompanyData}
				onChange={(e) => {
					const newValue = removeSpaces(e.target.value).toUpperCase();
					setShowClassicCompanyForm(false);
					setUserInfo((prevState: any) => ({
						...prevState,
						company: {
							...prevState.company,
							nbtva: newValue,
							siret: '',
							siren: '',
						},
					}));
				}}
				wrong={wrongFields.nbtva}
				wrongMessage={t('secondPanel:fill_this_field')}
			/>
		);
	}, [
		fetchingCompanyData,
		isFR,
		lookupTelemetry,
		syncExistingCompanyMatchByVAT,
		userInfo.company.nbtva,
		userInfo.company.siret,
		wrongFields.nbtva,
		wrongFields.siret,
		setUserInfo,
		t,
	]);
	
	return (
		<div
			style={{
				marginTop: 18,
				width: 277,
				height: openedPanel === 'company information' ? '' : 20,
				background: '#FFFFFF',
				borderRadius: 10,
				position: 'relative',
				padding: 12,
				boxShadow: '0px 1px 6px #00000029',
			}}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					alignItems: 'center',
					cursor: 'pointer',
				}}
				onClick={() => setOpenedPanel(openedPanel === 'company information' ? '' : 'company information')}
			>
				<div
					style={{
						height: 20,
						display: 'flex',
						flexDirection: 'row',
						gap: 8,
					}}
				>
					{isSecondPanelValidated ? (
						<img
							src="/images/check-2.svg"
							alt="check"
							style={{
								width: 18,
								height: 16,
							}}
						/>
					) : (
						<div style={{ width: 18 }}/>
					)}
					<div
						style={{
							fontSize: 16,
							fontWeight: 'bold',
							display: 'flex',
							alignItems: 'center',
						}}
					>
						{t('secondPanel:company_info')}
					</div>
				</div>
				
				{openedPanel === 'company information' ? (
					<img
						src="/images/chevron-down.svg"
						alt="chevron-down"
						style={{ width: 16, height: 16, marginRight: 5 }}
					/>
				) : (
					<img
						src="/images/chevron-right.svg"
						alt="chevron-right"
						style={{
							width: 20,
							height: 20,
							marginRight: 5
						}}
					/>
				)}
			</div>
			
			{openedPanel === 'company information' && (
				<>
					<div
						style={{
							margin: '20px 10px 0px 10px',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<CountrySelectInputMobile
							width={260}
							value={userInfo.company.countryCode}
							options={allCountriesList}
							title={t('secondPanel:country')}
							mandatory
							currentLanguage={i18n.language}
							onChange={(e) => {
								setUserInfo((prevState: any) => ({
									...prevState,
									company: {
										...prevState.company,
										countryCode: e.target.value,
										siret: '',
										siren: '',
										companyName: '',
										nbtva: '',
										address: '',
										postalCode: '',
										city: '',
									},
								}));
											void syncExistingCompanyMatchByVAT(undefined, lookupTelemetry);
								}}
							/>
						<div style={{ height: 18 }}></div>
						{blockIdentifier}
						{!isReducedLookupMode && userInfo.company.countryCode === 'FR' && (
							<>
								<div style={{ height: 18 }}></div>
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										gap: 10,
									}}
								>
									{blockNbtva}
									{blockSiren}
								</div>
							</>
						)}
						{!isReducedLookupMode && (
							<>
								<div style={{ height: 18 }}></div>
								{blockCompanyName}
								<div style={{ height: 18 }}></div>
								{blockAddress}
								<div style={{ height: 18 }}></div>
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										gap: 10,
									}}
								>
									{blockCity}
									{blockPostalCode}
								</div>
								<div style={{ height: 18 }}></div>
								<div
									style={{
										width: '100%',
										textAlign: 'center',
									}}
								>
									<button
										style={{
											textAlign: 'center',
											height: 28,
											width: 121,
											borderRadius: 5,
											backgroundColor: '#FF4D00',
											color: '#FFFFFF',
											border: 'none',
											fontSize: 14,
											fontWeight: 'bold',
											cursor: 'pointer',
											textTransform: 'uppercase',
										}}
										onClick={() => {
											if (validateFields()) {
												validateSecondPanel();
											}
										}}
									>
										{t('secondPanel:validate_button')}
									</button>
								</div>
							</>
						)}
					</div>
				</>
			)}
		</div>
	);
};

export default SignUpPageMobileProPanel2;
